import moment from "moment";
import * as R from "ramda";
import CryptoJS from "crypto-js";
import TripleDES from "crypto-js/tripledes";
import { Fraction } from "fractional";

export const now = () => moment().format("x") * 1;

// Converts from degrees to radians.
export const radians = (degrees) => (degrees * Math.PI) / 180;

// Converts from radians to degrees.
export const degrees = (radians) => (radians * 180) / Math.PI;

export const lpad = (value, padding) => {
  let zeroes = new Array(padding + 1).join("0");
  return (zeroes + value).slice(-padding);
};

export const getLocalStorageInfo = (databaseName) => {
  let localStorageInfo = window
    ? {
        spectre_country: window.localStorage.getItem("spectre_country"),
        spectre_language: window.localStorage.getItem("spectre_language"),
        spectre_lastcustomerInfo: window.localStorage.getItem(
          "spectre_lastcustomerInfo"
        ),
        spectre_user_settings: window.localStorage.getItem(
          "spectre_user_settings"
        ),
        spectre_userSession: window.localStorage.getItem("spectre_userSession"),
        spectre_userSessionId: window.localStorage.getItem(
          "spectre_userSessionId"
        ),
        spectre_version: window.localStorage.getItem("spectre_version"),
        databaseName,
      }
    : { databaseName };

  return JSON.stringify(localStorageInfo);
};

export const formatPhoneNumber = (phoneNumberString) => {
  let cleaned = ("" + phoneNumberString).replace(/\D/g, "");
  let match = cleaned.match(/^(1|)?(\d{3})(\d{3})(\d{4})$/);
  if (match) {
    let intlCode = match[1] ? "+1 " : "";
    return [intlCode, "(", match[2], ") ", match[3], "-", match[4]].join("");
  }
  return phoneNumberString;
};

export const setLocalStorageInfo = (localStorageInfoString) => {
  const localStorageInfo = JSON.parse(localStorageInfoString);
  if (window) {
    window.localStorage.setItem(
      "spectre_country",
      localStorageInfo.spectre_country
    );
    window.localStorage.setItem(
      "spectre_language",
      localStorageInfo.spectre_language
    );
    window.localStorage.setItem(
      "spectre_lastcustomerInfo",
      localStorageInfo.spectre_lastcustomerInfo
    );
    window.localStorage.setItem(
      "spectre_user_settings",
      localStorageInfo.spectre_user_settings
    );
    window.localStorage.setItem(
      "spectre_userSession",
      localStorageInfo.spectre_userSession
    );
    window.localStorage.setItem(
      "spectre_userSessionId",
      localStorageInfo.spectre_userSessionId
    );
    window.localStorage.setItem(
      "spectre_version",
      localStorageInfo.spectre_version
    );
  }

  return localStorageInfo;
};

export const _toBlob = (bString) => {
  let l, d, array;
  d = bString;
  l = d.length;
  array = new Uint8Array(l);
  for (let i = 0; i < l; i++) {
    array[i] = d.charCodeAt(i);
  }
  return new Blob([array], { type: "application/octet-stream" });
};

export const b64toBlob = (b64Data, contentType, sliceSize) => {
  contentType = contentType || "";
  sliceSize = sliceSize || 512;

  const byteCharacters = atob(b64Data);
  const byteArrays = [];

  for (let offset = 0; offset < byteCharacters.length; offset += sliceSize) {
    let slice = byteCharacters.slice(offset, offset + sliceSize);

    let byteNumbers = new Array(slice.length);
    for (let i = 0; i < slice.length; i++) {
      byteNumbers[i] = slice.charCodeAt(i);
    }

    let byteArray = new Uint8Array(byteNumbers);

    byteArrays.push(byteArray);
  }

  return new Blob(byteArrays, { type: contentType });
};

export const encryptSession = (userSession, dataId) => {
  const encryptedSession = TripleDES.encrypt(
    JSON.stringify(userSession),
    dataId
  );

  window.localStorage.setItem("spectre_userSession", encryptedSession);
  window.localStorage.setItem("spectre_userSessionId", dataId);
};

export const deleteSession = () => {
  window.localStorage.removeItem("spectre_userSession");
  window.localStorage.removeItem("spectre_userSessionId");
};

export const decryptSession = () => {
  const encryptedSession = window.localStorage.getItem("spectre_userSession");
  const sessionId = window.localStorage.getItem("spectre_userSessionId");

  if (encryptedSession) {
    return JSON.parse(
      TripleDES.decrypt(encryptedSession, sessionId).toString(CryptoJS.enc.Utf8)
    );
  } else {
    return;
  }
};

export const decryptSessionCLI = (encryptedSession, sessionId) => {
  if (encryptedSession) {
    return JSON.parse(
      TripleDES.decrypt(encryptedSession, sessionId).toString(CryptoJS.enc.Utf8)
    );
  } else {
    return;
  }
};

export const getDatabaseName = (name, version) => `v${version}_${name}`;

export const specSheetDecimalValues = (idx_data, fraction) => {
  const result = R.find(R.propEq("fraction", fraction))(idx_data);
  // // console.log(result);
  if (result) {
    return result.decimal;
  } else {
    return 0;
  }
};

export const incrementFraction = (fraction, increment) => {
  const fractionValue = new Fraction(fraction);
  const incrementValue = new Fraction(increment);

  const result = fractionValue.add(incrementValue);

  // console.log(result);
  if (result) {
    return result.toString();
  } else {
    return "";
  }
};

export const calculateFlexPitch = (
  f1_span_full,
  thm_flex,
  idx_data_span_measurement
) => {
  const initialFraction = {
    65: "2",
    75: "2",
    90: "2 1/8",
    105: "2 1/4",
    120: "2 3/8",
    135: "2 1/2",
    PRO: "2 5/8",
  };

  // console.log(f1_span_full, thm_flex, idx_data_span_measurement);

  const spanDecimal = specSheetDecimalValues(
    idx_data_span_measurement,
    f1_span_full
  );
  let spanFraction = new Fraction(spanDecimal);
  const baseDenominator = spanFraction.denominator;

  // console.log(spanDecimal, spanFraction, baseDenominator);

  while (spanFraction.denominator > 8) {
    spanFraction = spanFraction.subtract(new Fraction(1, baseDenominator));
  }
  let startFraction = new Fraction(initialFraction[thm_flex]);

  // console.log(spanFraction, startFraction);

  let multFraction = spanFraction.subtract(startFraction);
  let multiplier = multFraction.numerator * (8 / multFraction.denominator);

  let pitch = "";
  if (multiplier < 16) {
    pitch = new Fraction(15, 16)
      .subtract(new Fraction(multiplier, 16))
      .toString();

    if (multiplier < 15) {
      return {
        thm_for: pitch,
        thm_rev: "",
      };
    } else {
      return {
        thm_for: "",
        thm_rev: "",
      };
    }
  } else {
    multiplier -= 15;
    pitch = new Fraction(multiplier, 16).toString();

    return {
      thm_rev: pitch,
      thm_for: "",
    };
  }
};

export const findInsertSize = (size, idx_sizes_data) => {
  // console.log("findInsertSize", size, idx_sizes_data);
  const result = R.find((e) => {
    return e.size && size ? e.size.trim() === size.trim() : false;
  })(idx_sizes_data);

  return R.propOr("", "od", result);
};

export const getValue = (dcValue) => {
  if (typeof dcValue === "string") {
    return dcValue;
  }

  const cleanDcValue = JSON.parse(JSON.stringify(dcValue));

  return R.pipe(R.values, R.join(""))(cleanDcValue);
};

export const getPhotoForUserId = (userId) => (photo) => {
  if (!photo || photo === "") return null;

  const filePathArray = photo.split("/");

  if (filePathArray.length > 0) {
    const fileName = filePathArray[filePathArray.length - 1];
    return `https://app.spectrebowling.com/images/${userId}/${fileName}`;
  } else {
    return "";
  }
};

export const getPhotoForBall = (userId) => (photo) => {
  if (!photo || photo === "") return null;
  const filePathArray = photo.split("/");

  if (filePathArray.length > 0) {
    const fileName = filePathArray[filePathArray.length - 1];
    return `https://app.spectrebowling.com/images/${userId}/${fileName}`;
  } else {
    return "";
  }
};

export const validateValue = (value, defaultValue = "") => {
  if (value === "" || value === null || value === undefined) {
    return defaultValue;
  } else {
    return value;
  }
};

export const validateAndNormalizeValue = (value, defaultValue = "") => {
  if (value === "" || value === null || value === undefined) {
    return Number.parseFloat(defaultValue).toFixed(3);
  } else {
    return Number.parseFloat(value).toFixed(3);
  }
};

export const newBowler = () => {
  // console.log("new bowler");
};
export const newBall = () => {
  // console.log("new ball");
};
export const newSpecSheet = () => {
  // console.log("new spec sheet");
};

export const getVideoSettings = () => {
  // console.log("show video settings");
  window.open("https://youtu.be/nIiqe2jVuyw", "_blank");
};

export const checkValidValue = (validValue) =>
  validValue === undefined || validValue === "";

export const isValid = (values) => {
  for (const k in values) {
    if (!checkValidValue(values[k])) {
      return false;
    }
  }

  return true;
};
